import { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, X, Loader2, FileAudio } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useTranscriptions } from '@/hooks/useTranscriptions';
import { cn } from '@/lib/utils';

const ACCEPTED_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/webm',
  'audio/ogg',
  'audio/m4a',
  'audio/x-m4a',
  'audio/mp4',
  'audio/flac',
];

const DEFAULT_MAX_SIZE_MB = 30;
const EXTENDED_MAX_SIZE_MB = 100;

interface AudioUploadProps {
  onUploadComplete: (transcriptionId: string) => void;
  allowLargeFiles?: boolean;
}

export const AudioUpload = ({ onUploadComplete, allowLargeFiles = false }: AudioUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { createTranscription, startProcessing } = useTranscriptions();

  const maxSizeMB = allowLargeFiles ? EXTENDED_MAX_SIZE_MB : DEFAULT_MAX_SIZE_MB;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Ongeldig bestandstype. Alleen audio bestanden (MP3, M4A, WAV, WebM, OGG) zijn toegestaan.';
    }
    if (file.size > maxSizeBytes) {
      return `Bestand is te groot. Maximum grootte is ${maxSizeMB}MB.`;
    }
    return null;
  };

  const handleFile = (selectedFile: File) => {
    const error = validateFile(selectedFile);
    if (error) {
      toast({
        title: "Ongeldig bestand",
        description: error,
        variant: "destructive",
      });
      return;
    }
    
    // Show warning for large files
    if (selectedFile.size > DEFAULT_MAX_SIZE_MB * 1024 * 1024) {
      toast({
        title: "Groot bestand",
        description: "Dit bestand is groter dan 30MB. Verwerking kan langer duren.",
      });
    }
    
    setFile(selectedFile);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  }, [maxSizeBytes]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    
    try {
      // Generate unique storage path
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `${timestamp}_${safeName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('audio-files')
        .upload(storagePath, file);

      if (uploadError) {
        throw new Error(`Upload mislukt: ${uploadError.message}`);
      }

      // Create database record
      const result = await createTranscription.mutateAsync({
        filename: file.name,
        storagePath,
      });

      // Start background processing
      await startProcessing(result.id, storagePath);

      toast({
        title: "Upload gestart",
        description: "Je audio wordt nu verwerkt. Dit kan enkele minuten duren.",
      });

      // Clear file and notify parent
      setFile(null);
      onUploadComplete(result.id);

    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Fout bij uploaden",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Audio Uploaden
          {allowLargeFiles && (
            <span className="text-xs bg-amber-500/20 text-amber-600 px-2 py-0.5 rounded-full font-normal">
              Extended: {maxSizeMB}MB
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            isDragOver 
              ? "border-primary bg-primary/5" 
              : "border-muted-foreground/25 hover:border-primary/50"
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            onChange={handleInputChange}
            className="hidden"
          />
          
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileAudio className="h-8 w-8 text-primary" />
              <div className="text-left">
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFile();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <FileAudio className="h-12 w-12 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground">
                Sleep een audiobestand hierheen of klik om te selecteren
              </p>
              <p className="text-sm text-muted-foreground">
                MP3, M4A, WAV, WebM, OGG • Max {maxSizeMB}MB
              </p>
            </div>
          )}
        </div>

        <Button
          onClick={handleUpload}
          disabled={!file || isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Bezig met uploaden...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Verwerken
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
